import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PiArrowRight,
  PiCheckCircle,
  PiCheckCircleFill,
  PiMagnifyingGlass,
  PiShuffle,
  PiStar,
  PiStarFill,
  PiTrash,
  PiX,
} from "react-icons/pi";
import { StoryDescriptor, truncateStoryListTitle } from "./story";
import { levelBadgeClasses } from "./levelColors";
import { lstr } from "./localization";
import {
  FINISHED_PROGRESS,
  ReadingEntry,
  isInProgress,
} from "./readingProgress";
import { useSearchParamsState } from "./searchParamsState";

// Building blocks for lists of stories: titles, generated covers, rows, the
// library with search and filters, and the daily featured story.

export function titleForLocale(
  s: StoryDescriptor,
  locale: string,
): string | undefined {
  const idx = s.locales.indexOf(locale);
  return idx === -1 ? undefined : s.titles[idx];
}

// primaryTitle is the story's title in the learned language (r), falling back
// to its first title: stories generated without a mother tongue have only the
// r title. Returns the (truncated) title and the locale it is written in.
export function primaryTitle(
  s: StoryDescriptor,
  r: string,
): { title: string; locale: string } {
  const rTitle = titleForLocale(s, r);
  return rTitle !== undefined
    ? { title: truncateStoryListTitle(rTitle), locale: r }
    : { title: truncateStoryListTitle(s.titles[0]), locale: s.locales[0] };
}

// A descriptor whose titles don't line up with its locales can't be shown.
export function isListable(s: StoryDescriptor): boolean {
  return s.locales.length === s.titles.length && s.locales.length >= 1;
}

// isFinished says whether a story counts as read: marked read on the server
// (logged-in users) or read to the end on this device.
export function isFinished(
  s: StoryDescriptor,
  progressByStoryId: Map<string, number>,
): boolean {
  return s.read || (progressByStoryId.get(s.id) ?? 0) >= FINISHED_PROGRESS;
}

// hashString is FNV-1a: a fast, stable string hash for picking cover designs
// and daily stories deterministically.
export function hashString(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// "B2-C1" reads better with an en dash.
export function formatLevel(level: string): string {
  return level.replace("-", "–");
}

export function LevelBadge({
  level,
  className = "",
}: {
  level: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-px text-[11px] font-semibold leading-4 tracking-wide ${levelBadgeClasses(level)} ${className}`}
    >
      {formatLevel(level)}
    </span>
  );
}

// Muted paper-and-ink tones for generated covers: a base dark enough for a
// cream title, and a lighter accent for the pattern.
const COVER_PALETTES: { base: string; accent: string }[] = [
  { base: "#5f7d66", accent: "#9db8a2" },
  { base: "#a3573d", accent: "#d98e70" },
  { base: "#94701f", accent: "#d4a94e" },
  { base: "#4f6886", accent: "#8aa3c2" },
  { base: "#6e5172", accent: "#a888ab" },
  { base: "#3d6f70", accent: "#77a8a8" },
  { base: "#9c5465", accent: "#d68f9e" },
  { base: "#66662f", accent: "#a3a35e" },
  { base: "#3f6650", accent: "#79a389" },
  { base: "#87573f", accent: "#c48d70" },
  { base: "#4a5a7a", accent: "#8796b8" },
  { base: "#7a4f3a", accent: "#b5876d" },
];

// Cover patterns on a 60x80 canvas, drawn in the accent color.
const COVER_PATTERNS: ((accent: string, base: string) => JSX.Element)[] = [
  // Sun over a horizon.
  (accent) => (
    <>
      <circle cx="42" cy="24" r="14" fill={accent} opacity="0.9" />
      <path d="M0 60 Q30 52 60 60 V80 H0 Z" fill={accent} opacity="0.4" />
    </>
  ),
  // Rolling hills.
  (accent) => (
    <>
      <path
        d="M-5 60 Q15 42 35 60 T75 60 V80 H-5 Z"
        fill={accent}
        opacity="0.5"
      />
      <path
        d="M-5 70 Q20 55 45 70 T95 70 V80 H-5 Z"
        fill={accent}
        opacity="0.85"
      />
    </>
  ),
  // Diagonal stripes.
  (accent) => (
    <g stroke={accent} strokeWidth="5" opacity="0.4">
      <path d="M-10 30 L40 -20 M-10 52 L62 -20 M-10 74 L70 -6 M4 90 L70 24 M26 90 L70 46" />
    </g>
  ),
  // Concentric arcs from a corner.
  (accent) => (
    <g fill="none" stroke={accent} strokeWidth="3" opacity="0.55">
      <circle cx="0" cy="80" r="18" />
      <circle cx="0" cy="80" r="30" />
      <circle cx="0" cy="80" r="42" />
      <circle cx="0" cy="80" r="54" />
    </g>
  ),
  // A field of dots.
  (accent) => (
    <g fill={accent} opacity="0.6">
      {Array.from({ length: 6 }, (_, row) =>
        Array.from({ length: 5 }, (_, col) => (
          <circle
            key={`${row}:${col}`}
            cx={10 + col * 10 + (row % 2) * 5}
            cy={10 + row * 9}
            r="1.7"
          />
        )),
      )}
    </g>
  ),
  // Waves.
  (accent) => (
    <g
      fill="none"
      stroke={accent}
      strokeWidth="2.5"
      opacity="0.65"
      strokeLinecap="round"
    >
      <path d="M4 18 Q11 12 18 18 T32 18 T46 18 T60 18" />
      <path d="M4 28 Q11 22 18 28 T32 28 T46 28 T60 28" />
      <path d="M4 38 Q11 32 18 38 T32 38 T46 38 T60 38" />
    </g>
  ),
  // A crescent moon.
  (accent, base) => (
    <>
      <circle cx="36" cy="26" r="14" fill={accent} opacity="0.9" />
      <circle cx="42" cy="21" r="12" fill={base} />
      <circle cx="14" cy="14" r="1.2" fill={accent} />
      <circle cx="22" cy="40" r="0.9" fill={accent} />
      <circle cx="50" cy="44" r="1.1" fill={accent} />
    </>
  ),
  // Mountains under a small sun.
  (accent) => (
    <>
      <circle cx="44" cy="20" r="6" fill={accent} opacity="0.9" />
      <path
        d="M-4 80 L20 40 L34 58 L44 46 L66 80 Z"
        fill={accent}
        opacity="0.55"
      />
      <path d="M8 80 L28 52 L48 80 Z" fill={accent} opacity="0.85" />
    </>
  ),
];

// StoryCover is a generated book cover: a palette and a pattern picked from
// the story id, so every story keeps the same cover everywhere. With a title
// it reads as a book; without one it is a thumbnail.
export function StoryCover({
  storyId,
  title,
  titleLang,
  className = "",
}: {
  storyId: string;
  title?: string;
  titleLang?: string;
  // Size and rounding; covers are portrait (3:4) by design.
  className?: string;
}) {
  const hash = hashString(storyId);
  const palette = COVER_PALETTES[hash % COVER_PALETTES.length];
  const pattern =
    COVER_PATTERNS[
      Math.floor(hash / COVER_PALETTES.length) % COVER_PATTERNS.length
    ];
  return (
    <div
      aria-hidden="true"
      className={`relative overflow-hidden shadow-card ${className}`}
      style={{ backgroundColor: palette.base }}
    >
      <svg
        viewBox="0 0 60 80"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        {pattern(palette.accent, palette.base)}
        {/* Spine: a shaded band and a highlight along the left edge. */}
        <rect x="0" y="0" width="3.5" height="80" fill="#000" opacity="0.14" />
        <rect
          x="3.5"
          y="0"
          width="0.8"
          height="80"
          fill="#fff"
          opacity="0.14"
        />
      </svg>
      {title && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/45 to-transparent px-3 pb-3 pt-8">
          <div
            lang={titleLang}
            className="font-literata text-[15px] font-bold leading-snug text-[#fffaf0] line-clamp-4 [text-wrap:balance]"
          >
            {title}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProgressBar({
  value,
  className = "",
}: {
  // 0..1
  value: number;
  className?: string;
}) {
  return (
    <div
      className={`h-1 overflow-hidden rounded-full bg-cream-dark ${className}`}
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-500"
        style={{
          width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%`,
        }}
      />
    </div>
  );
}

// StoryRow is one story in a grouped list: cover thumbnail, learned-language
// (r) title with an optional mother-tongue (l) subtitle, level and reading
// progress, plus optional favorite, read and delete controls; a read story's
// title is muted. The title is a real link whose ::after overlay covers the
// whole row, so the row stays one big tap target (and can be opened in a new
// tab or shared), while the controls sit above the overlay as separate
// buttons instead of being nested inside another interactive element.
export function StoryRow({
  s,
  l,
  r,
  progress,
  onDelete,
  onToggleFavorite,
  onToggleRead,
}: {
  s: StoryDescriptor;
  l: string;
  r: string;
  // Reading progress on this device, 0..1, if the story was opened.
  progress?: number;
  onDelete?: (storyId: string) => void;
  // When set, a star toggle is shown; called with the new desired state.
  onToggleFavorite?: (storyId: string, favorite: boolean) => void;
  // When set, a read check toggle is shown; called with the new desired state.
  onToggleRead?: (storyId: string, read: boolean) => void;
}) {
  const strings = lstr(l);
  const primary = primaryTitle(s, r);
  const secondaryTitleForLocale =
    primary.locale === l ? undefined : titleForLocale(s, l);
  const secondaryTitle =
    secondaryTitleForLocale === undefined
      ? undefined
      : truncateStoryListTitle(secondaryTitleForLocale);
  const inProgress = !s.read && isInProgress(progress);
  const controlClass =
    "relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-cream-dark";
  return (
    <div className="relative flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-cream-dark/50 sm:px-4">
      <StoryCover
        storyId={s.id}
        className="h-[52px] w-10 shrink-0 rounded-[5px]"
      />
      <div className="flex min-w-0 flex-grow flex-col gap-0.5 text-left">
        <Link
          to={`/${s.id}`}
          lang={primary.locale}
          className={`font-literata text-[15.5px] font-semibold leading-snug line-clamp-2 after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary ${
            s.read ? "text-secondary-text" : "text-main-text"
          }`}
        >
          {primary.title}
        </Link>
        {secondaryTitle && (
          <div lang={l} className="truncate text-sm text-secondary-text">
            {secondaryTitle}
          </div>
        )}
        <div className="mt-0.5 flex items-center gap-2">
          <LevelBadge level={s.level} />
          {inProgress && (
            <>
              <ProgressBar value={progress!} className="w-14" />
              <span className="text-xs text-secondary-text">
                {Math.round(progress! * 100)}%
              </span>
            </>
          )}
          {s.favorite && !onToggleFavorite && (
            <PiStarFill className="text-amber-400" size={13} />
          )}
        </div>
      </div>
      {onToggleFavorite && (
        <button
          type="button"
          aria-label={
            s.favorite
              ? strings.unfavorite_story_button_label
              : strings.favorite_story_button_label
          }
          aria-pressed={s.favorite}
          className={`${controlClass} ${
            s.favorite
              ? "text-amber-400 hover:text-amber-500"
              : "text-secondary-text/70 hover:text-amber-500"
          }`}
          onClick={() => onToggleFavorite(s.id, !s.favorite)}
        >
          {s.favorite ? <PiStarFill size={18} /> : <PiStar size={18} />}
        </button>
      )}
      {onToggleRead && (
        <button
          type="button"
          aria-label={
            s.read
              ? strings.mark_story_unread_button_label
              : strings.mark_story_read_button_label
          }
          aria-pressed={s.read}
          className={`${controlClass} ${
            s.read
              ? "text-primary hover:text-primary-hover"
              : "text-secondary-text/70 hover:text-primary"
          }`}
          onClick={() => onToggleRead(s.id, !s.read)}
        >
          {s.read ? (
            <PiCheckCircleFill size={19} />
          ) : (
            <PiCheckCircle size={19} />
          )}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          aria-label={strings.delete_story_button_label}
          className={`${controlClass} text-secondary-text/70 hover:text-error`}
          onClick={() => onDelete(s.id)}
        >
          <PiTrash size={17} />
        </button>
      )}
    </div>
  );
}

// StoryRows groups rows into one card with hairline separators.
export function StoryRows({ children }: { children: React.ReactNode }) {
  return (
    <div className="card divide-y divide-border overflow-hidden">
      {children}
    </div>
  );
}

// Folds case and diacritics, so "grosser" finds "grösser".
function searchKey(text: string): string {
  return text
    .toLocaleLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/ß/g, "ss");
}

const CEFR_ORDER = ["A1", "A2", "B1", "B2", "C1", "C2"];

function levelRank(level: string): number {
  const rank = CEFR_ORDER.indexOf(level.split("-")[0].trim().toUpperCase());
  return rank === -1 ? CEFR_ORDER.length : rank;
}

// pickSurprise picks a random story the reader hasn't finished yet, or any
// story once everything is read.
function pickSurprise(
  stories: StoryDescriptor[],
  progressByStoryId: Map<string, number>,
): StoryDescriptor | undefined {
  const unread = stories.filter((s) => !isFinished(s, progressByStoryId));
  const pool = unread.length > 0 ? unread : stories;
  return pool[Math.floor(Math.random() * pool.length)];
}

// StoryLibrary is the browsable list of built-in stories: search, level
// filter, "Surprise me", and a short list that expands on request so the
// page isn't a wall of rows. Search, level and expansion are kept in the URL.
export function StoryLibrary({
  l,
  r,
  stories,
  progressByStoryId,
  onToggleFavorite,
  onToggleRead,
  initiallyVisible = 8,
}: {
  l: string;
  r: string;
  stories: StoryDescriptor[];
  progressByStoryId: Map<string, number>;
  onToggleFavorite?: (storyId: string, favorite: boolean) => void;
  onToggleRead?: (storyId: string, read: boolean) => void;
  initiallyVisible?: number;
}) {
  const strings = lstr(l);
  const navigate = useNavigate();
  const [searchParams, updateSearchParams] = useSearchParamsState();
  const search = searchParams.get("search") ?? "";
  const setSearch = (value: string) =>
    updateSearchParams({ search: value || null });
  const setLevel = (value: string | null) =>
    updateSearchParams({ level: value });
  const expanded = searchParams.get("expanded") === "1";
  const expand = () => updateSearchParams({ expanded: "1" });

  const listable = useMemo(() => stories.filter(isListable), [stories]);
  const levels = useMemo(
    () =>
      Array.from(new Set(listable.map((s) => s.level))).sort(
        (a, b) => levelRank(a) - levelRank(b) || a.localeCompare(b),
      ),
    [listable],
  );
  const levelParam = searchParams.get("level");
  const level =
    levelParam !== null && levels.includes(levelParam) ? levelParam : null;
  const searchTerm = searchKey(search.trim());
  const filtered = listable.filter(
    (s) =>
      (level === null || s.level === level) &&
      (searchTerm === "" ||
        s.titles.some((title) => searchKey(title).includes(searchTerm))),
  );
  const showAll = expanded || searchTerm !== "";
  const visible = showAll ? filtered : filtered.slice(0, initiallyVisible);
  // Search and filters only help in a long list.
  const browsable = listable.length > initiallyVisible;

  const surprise = () => {
    const story = pickSurprise(
      filtered.length > 0 ? filtered : listable,
      progressByStoryId,
    );
    if (story) navigate(`/${story.id}`);
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-literata text-2xl font-bold text-main-text">
          {strings.library_header}
        </h2>
        {listable.length >= 3 && (
          <button
            type="button"
            onClick={surprise}
            className="btn-ghost -mr-2 px-3 py-1.5 text-sm text-primary hover:text-primary"
          >
            <PiShuffle size={16} />
            {strings.library_surprise}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-secondary-text">
        {strings.library_subtitle}
      </p>

      {browsable && (
        <div className="mt-4 flex flex-col gap-3">
          <label className="relative block">
            <span className="sr-only">{strings.library_search_label}</span>
            <PiMagnifyingGlass
              size={17}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-text"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={strings.library_search_placeholder}
              className="input py-2.5 pl-10 pr-10 text-[15px] [&::-webkit-search-cancel-button]:hidden"
            />
            {search && (
              <button
                type="button"
                aria-label={strings.library_search_clear}
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-secondary-text hover:bg-cream-dark hover:text-main-text"
              >
                <PiX size={14} />
              </button>
            )}
          </label>
          {levels.length > 1 && (
            <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
              <button
                type="button"
                aria-pressed={level === null}
                onClick={() => setLevel(null)}
                className={level === null ? "chip-selected" : "chip-idle"}
              >
                {strings.library_all_levels}
              </button>
              {levels.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={level === value}
                  onClick={() => setLevel(level === value ? null : value)}
                  className={level === value ? "chip-selected" : "chip-idle"}
                >
                  {formatLevel(value)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        {visible.length > 0 ? (
          <StoryRows>
            {visible.map((story) => (
              <StoryRow
                key={story.id}
                s={story}
                l={l}
                r={r}
                progress={progressByStoryId.get(story.id)}
                onToggleFavorite={onToggleFavorite}
                onToggleRead={onToggleRead}
              />
            ))}
          </StoryRows>
        ) : (
          <div className="card px-6 py-10 text-center text-secondary-text">
            {strings.library_no_results}
          </div>
        )}
        {!showAll && filtered.length > visible.length && (
          <button
            type="button"
            onClick={expand}
            className="btn-secondary mt-3 w-full py-3 text-[15px]"
          >
            {strings.library_show_all.replace(
              "{count}",
              String(filtered.length),
            )}
          </button>
        )}
      </div>
    </section>
  );
}

// The day's pick is remembered per learned language, so it stays put for the
// whole day even after it has been read (and then shows as done).
const STORY_OF_THE_DAY_KEY = "storyOfTheDay";

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

// storyOfTheDay picks one unfinished built-in story per day and language.
export function storyOfTheDay(
  stories: StoryDescriptor[],
  r: string,
  progressByStoryId: Map<string, number>,
): StoryDescriptor | undefined {
  const listable = stories.filter(isListable);
  const day = localDayKey(new Date());
  let remembered: Record<string, { day: string; storyId: string }> = {};
  try {
    const raw: unknown = JSON.parse(
      localStorage.getItem(STORY_OF_THE_DAY_KEY) ?? "{}",
    );
    if (raw && typeof raw === "object") remembered = raw as typeof remembered;
  } catch (err) {
    console.error("Failed to load the story of the day", err);
  }
  const previous = remembered[r];
  if (previous?.day === day) {
    const story = listable.find((s) => s.id === previous.storyId);
    if (story) return story;
  }
  const unread = listable.filter((s) => !isFinished(s, progressByStoryId));
  const pool = unread.length > 0 ? unread : listable;
  if (pool.length === 0) return undefined;
  const story = pool[hashString(`${day}:${r}`) % pool.length];
  try {
    localStorage.setItem(
      STORY_OF_THE_DAY_KEY,
      JSON.stringify({ ...remembered, [r]: { day, storyId: story.id } }),
    );
  } catch (err) {
    console.error("Failed to store the story of the day", err);
  }
  return story;
}

// FeaturedStoryCard presents one story as a book: a large cover beside its
// title and a call to read. Once the story is finished it says so, inviting
// the reader back for tomorrow's pick.
export function FeaturedStoryCard({
  story,
  l,
  r,
  eyebrow,
  finished,
}: {
  story: StoryDescriptor;
  l: string;
  r: string;
  eyebrow: string;
  finished: boolean;
}) {
  const strings = lstr(l);
  const primary = primaryTitle(story, r);
  const subtitle = primary.locale === l ? undefined : titleForLocale(story, l);
  return (
    <Link
      to={`/${story.id}`}
      className="card-interactive group flex items-stretch gap-4 p-4 sm:gap-5 sm:p-5"
    >
      <StoryCover
        storyId={story.id}
        title={primary.title}
        titleLang={primary.locale}
        className="h-36 w-[6.75rem] shrink-0 rounded-lg sm:h-40 sm:w-[7.5rem]"
      />
      <div className="flex min-w-0 flex-col py-1">
        <div className="eyebrow text-primary">{eyebrow}</div>
        <div
          lang={primary.locale}
          className="mt-1.5 font-literata text-xl font-bold leading-snug text-main-text line-clamp-3"
        >
          {primary.title}
        </div>
        {subtitle && (
          <div
            lang={l}
            className="mt-0.5 text-sm text-secondary-text line-clamp-2"
          >
            {subtitle}
          </div>
        )}
        <div className="mt-2">
          <LevelBadge level={story.level} />
        </div>
        <div className="mt-auto pt-3 text-sm font-semibold text-primary">
          {finished ? (
            <span className="inline-flex items-center gap-1.5">
              <PiCheckCircleFill size={17} />
              {strings.featured_story_done}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              {strings.featured_story_read}
              <PiArrowRight
                size={15}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// upNextStories suggests what to read after a story: unfinished stories of
// the same level first. The order is stable for a given story, so the
// suggestions don't reshuffle while the reader looks at them.
export function upNextStories(
  stories: StoryDescriptor[],
  currentStoryId: string,
  progressByStoryId: Map<string, number>,
  count: number,
): StoryDescriptor[] {
  const current = stories.find((s) => s.id === currentStoryId);
  return stories
    .filter(
      (s) =>
        isListable(s) &&
        s.id !== currentStoryId &&
        !isFinished(s, progressByStoryId),
    )
    .map((s) => ({
      s,
      sameLevel: current !== undefined && s.level === current.level,
      order: hashString(`${currentStoryId}:${s.id}`),
    }))
    .sort(
      (a, b) => Number(b.sameLevel) - Number(a.sameLevel) || a.order - b.order,
    )
    .slice(0, count)
    .map(({ s }) => s);
}

// ContinueReadingCard takes the reader back into the story they were in the
// middle of, at the place they left it.
export function ContinueReadingCard({
  l,
  entry,
}: {
  l: string;
  entry: ReadingEntry;
}) {
  const strings = lstr(l);
  const percent = Math.round(entry.progress * 100);
  return (
    <Link
      to={`/${entry.storyId}`}
      className="card-interactive group flex items-center gap-4 p-4"
    >
      <StoryCover
        storyId={entry.storyId}
        className="h-[72px] w-14 shrink-0 rounded-md"
      />
      <div className="min-w-0 flex-grow">
        <div className="eyebrow text-primary">{strings.continue_reading}</div>
        <div
          lang={entry.r}
          className="mt-0.5 truncate font-literata text-[17px] font-semibold text-main-text"
        >
          {entry.rTitle}
        </div>
        <div className="mt-2 flex items-center gap-2.5">
          <ProgressBar value={entry.progress} className="flex-grow" />
          <span className="shrink-0 text-xs tabular-nums text-secondary-text">
            {strings.percent_read.replace("{percent}", String(percent))}
          </span>
        </div>
      </div>
      <PiArrowRight
        size={18}
        className="shrink-0 text-secondary-text transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary"
      />
    </Link>
  );
}
